<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import type { Rating, Place, Client } from '~/types/types'
import Vue3StarRatings from '~/components/Vue3StarRatings.vue'
import { useAuthStore } from '@/store/auth'
import { ratingsRepository } from '~/utils/ratingsRepository'
import { useBootstrapModal } from '@/composables/useBootstrapModal'
import { useWarningModal } from "~/composables/useWarningModal"
import { defineProps } from 'vue'


/* ========================
   Props Definition
   ======================== */
const props = defineProps<{
  rate: Rating
  userShow?: boolean
  placeShow?: boolean
  actionShow?: boolean
  filterText?: string
  classShow?: string
}>()



const highlightMatch = (text: string): string => {
  const keyword = props.filterText?.trim();
  if (!keyword) return text;

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape RegEx
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');

  return text.replace(regex, '<span class="highlight">$1</span>');
};

const emit = defineEmits<{
  (event: 'update:rate', rate: Rating): void;
  (event: 'open-report', ratingId: string): void;
}>()

/* ========================
   Repository, Store & Modal
   ======================== */
const ratingRepo = ratingsRepository()
const authStore = useAuthStore()
const currentUser = computed(() => authStore.user)
const { openModal: openUserModal } = useBootstrapModal("userModal")
const { showModal: ShowWarning } = useWarningModal()

// const { openModal: openReportModal } = useBootstrapModal("report_rate_modal")

/* ========================
   Utility Functions
   ======================== */
const slugify = (text: string): string =>
text ? text.trim().replace(/\s+/g, '-').toLowerCase() : ''

/* ========================
   Local Reactive State (initialized on mount)
   ======================== */
const isLiked = ref<boolean>(false)
const isReported = ref<boolean>(false)
const likesCount = ref<number>(0)

onMounted(() => {
  // Initialize local state from props
  isLiked.value = props.rate.is_liked
  isReported.value = props.rate.is_reported
  likesCount.value = props.rate.likes_count



})

/* ========================
   Computed Properties
   ======================== */
// Wrap the incoming prop for easier use
const rating = computed(() => props.rate)

// Build the user profile link (adjust logic as needed)
const userLink = computed((): string => {
  const client: Client = rating.value.client

    // const place: Place = rating.value.place
  return `/user-profile/${client.id}/${slugify(client.user_name)}`
})

// Build the place details link using slugified name
const placeLink = computed((): string => {
  const place: Place = rating.value.place
  return `/place/${place.tenant}`
})

// Reactive flag to trigger animation on like toggle
const likeAnimation = ref(false)

// Updated computed property for the like button classes using local state
const likeClasses = computed((): string => {
  let classes = 'likes'
  classes += isLiked.value ? ' liked' : ' like'

  if (currentUser.value?.id == rating.value.client_id) {
    classes += ' ban-like' 
  } else {
    classes += ' add-like' 
  }

  if (likeAnimation.value) {
    classes += ' animating'
  }
  return classes
})


const isRateUser = computed((): boolean => {
  
  if (currentUser.value?.id == rating.value.client_id) {
    return true
  } else {
    return false
  }

})

// API endpoint for liking (adjust if necessary)
const likeUrl = computed((): string => '/')

// Image gallery computed properties with safe array checks
const imageCount = computed((): number =>
  rating.value.images ? rating.value.images.length : 0
)
const hiddenImageCount = computed((): number =>
  imageCount.value > 4 ? imageCount.value - 4 : 0
)
const hiddenImageText = computed((): string =>
  hiddenImageCount.value > 0 ? ` + ${hiddenImageCount.value}` : ' + '
)
const galleryClass = computed((): string => {
  switch (imageCount.value) {
    case 1:
      return 'one-image'
    case 2:
      return 'two-images'
    case 3:
      return 'three-images'
    default:
      return imageCount.value >= 4 ? 'four-images' : ''
  }
})

// Split images into visible (first 4) and hidden arrays safely
const visibleImages = computed(() =>
  rating.value.images ? rating.value.images.slice(0, 4) : []
)
const hiddenImages = computed(() =>
  rating.value.images ? rating.value.images.slice(4) : []
)

/* ========================
   Reactive State
   ======================== */
const copyMessage = ref<string>('')

/* ========================
   Methods
   ======================== */
// Toggle like status (simulate an API call)
async function toggleLike(): Promise<void> {
  if (!currentUser.value) {
    openUserModal()
    return
  }

  if (isRateUser.value) {
    return
  }

  try {
    const response = await ratingRepo.toggleLikes(rating.value.id)
    if (response.response.status) {
      // Trigger the like animation
      likeAnimation.value = true
      setTimeout(() => {
        likeAnimation.value = false
      }, 1000) // Duration of the animation in ms

      // Toggle the like status locally and update likes count
      isLiked.value = !isLiked.value
      likesCount.value = response.response.data.likes_count

   
    } else {
      console.error('Unexpected response structure:', response)
    }
  } catch (error) {
    console.error('Error toggling like:', error)
  }
}

 function handleReport() {
  

  if(!currentUser.value){
    openUserModal()
    return
  }

  if (isRateUser.value) {
    ShowWarning('لا يمكن عمل شكوى على تقييماتك', 'لا يمكن عمل شكوى على تقييماتك')
    return
  }
  
  if (isReported.value) {
    ShowWarning('لا يمكن عمل شكوى مرتين', 'لا يمكن عمل شكوى مرتين')
    return
  }

  try {
    emit('open-report', rating.value.id) 

  } catch (error) {
    console.error('Error handle Report: ', error)

  }
}


// Copy the current page URL to the clipboard
async function copyUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(window.location.href)
    copyMessage.value = 'تم النسخ'
    setTimeout(() => {
      copyMessage.value = ''
    }, 2000)
  } catch (error) {
    console.error('Error copying URL: ', error)
  }
}


const showChar = 245;
const showCharLg = 320;
const ellipsestext = "...";
const moretext = "عرض المزيد";
const lesstext = "";

const expanded = ref(false);

const hasMore = ref(false);


const truncatedText = computed(() => {
  const limit = props.userShow ? showCharLg : showChar;

  let reviewText = ref('');

  if (props.rate.review.length > limit) {
    hasMore.value = true;
    reviewText.value = props.rate?.review?.substring(0, limit);
    
  } else {
    reviewText.value = props.rate.review;
  }

  return reviewText.value;

});

const toggleExpand = () => {
  expanded.value = !expanded.value;
};


</script>

<template>
  <div :class="userShow ? 'col-md-12' : (props.classShow ? props.classShow : 'col-md-6')">
    <div class="item_rate version-1 multi_image_wrapper animate">
      <!-- Header -->
      <div class="item_rate_head">
        <div class="item_image">
          <NuxtLink v-if="rating.client" :to="userLink">

            <NuxtImg
              :src="rating.client.thumbnail"
              :alt="rating.client.user_name"
              loading="lazy"
              :custom="true"
              v-slot="{ src, isLoaded, imgAttrs }"
            >
              <!-- Show the actual image when loaded -->
              <img
                v-if="isLoaded"
                v-bind="imgAttrs"
                :src="src"
              >

              <!-- Show a placeholder while loading -->
              <img
                v-else
                :src="'/website/assets/img/avatar.png'"
              >
            </NuxtImg>


          </NuxtLink>
          <NuxtLink v-else-if="rating.place" :to="placeLink">

            
            <NuxtImg
              :src="rating.place.main_image"
              :alt="rating.place.name"
              loading="lazy"
              :custom="true"
              v-slot="{ src, isLoaded, imgAttrs }"
            >
              <!-- Show the actual image when loaded -->
              <img
                v-if="isLoaded"
                v-bind="imgAttrs"
                :src="src"
              >

              <!-- Show a placeholder while loading -->
              <img
                v-else
                :src="'/website/assets/img/default-featured-image.jpeg'"
              >
            </NuxtImg>

          </NuxtLink>
        </div>
        <div :class="['item_content', userShow ? 'w-100' : '']">
          <h3>
            <NuxtLink v-if="rating.client" :to="userLink">
              
              {{ rating.client.user_name || '-' }}
            </NuxtLink>
            <NuxtLink v-else-if="rating.place" :to="placeLink">
              {{ rating.place.name || '-' }}
            </NuxtLink>
          </h3>
          <div class="date_likes">
            <div class="date">
              <span>{{ rating.since }}</span>
            </div>
          </div>
          <div class="stars">
            <Vue3StarRatings
              :modelValue="rating.stars"
              :numberOfStars="5"
              :starSize="18"
              :disableClick="true"
            />
            <span class="rate">( {{ rating.stars.toFixed(1) }} )</span>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="item_rate_body">
        <div class="item_content w-100">
          <div v-if="rating.review" class="text-container">
            <p class="text_item text">
              <span v-if="expanded" v-html="highlightMatch(rating.review)"></span>
              <span v-else >
                <span v-html="highlightMatch(truncatedText)">
                </span>
                <span class="moreelipses" v-if="hasMore">{{ ellipsestext }}</span>
              </span>
              <a href="javascript:void(0)" @click.prevent="toggleExpand" class="toggle-button" v-if="hasMore">
                {{ expanded ? lesstext : moretext }}
              </a>
            </p>
          </div>
          <div v-else class="empty-review-text"></div>
          <div class="item_rate_body_attach">
            <!-- Image Gallery -->
            <div class="attached_images" :class="{ hidden: imageCount === 0 }">
              <div
                :class="[galleryClass, 'rate-gallery-' + rating.id, 'rate-gallery', !userShow ? 'test' : '']"
                :data-image-count="imageCount"
              >
                <template v-for="(image, index) in visibleImages" :key="index">
                  <a
                    :href="image.large"
                    :data-fancybox="`image${rating.id}`"
                    :data-hidden-image-count="hiddenImageText"
                    :class="{ 'video-attachment': image.type === 'video' }"
                  >
                    <img
                      loading="lazy"
                      :src="image.medium"
                      class="image-slide-show"
                      :alt="rating.place.name || '-'"
                    />
                  </a>
                </template>
                <template v-if="hiddenImages.length">
                  <a
                    v-for="(image, index) in hiddenImages"
                    :key="'hidden-' + index"
                    :href="image.large"
                    :data-fancybox="`image${rating.id}`"
                    class="hidden-image"
                    style="display: none;"
                  >
                    <img
                      loading="lazy"
                      :src="image.medium"
                      class="image-slide-show"
                      :alt="rating.place.name || '-'"
                    />
                  </a>
                </template>
              </div>
            </div>
            <!-- Conditional Content -->
            <template v-if="userShow">
              <!-- Like Button -->
              <div class="date_likes">
                <div :class="likeClasses" @click="toggleLike">
                  <img
                    :id="`likeImage${rating.id}`"
                    :src="isLiked ? '/website/assets/img/icons/heart-bold.svg' : '/website/assets/img/icons/heart-linear.svg'"
                    alt="like"
                  />
                  <span :id="`likeCount${rating.id}`">
                    {{ likesCount }}
                  </span>
                  <svg
                    class="svg-celebrate"
                    :id="`fireworks${rating.id}`"
                    width="100"
                    height="100"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polygon points="10,10 20,20"></polygon>
                    <polygon points="10,50 20,50"></polygon>
                    <polygon points="20,80 30,70"></polygon>
                    <polygon points="90,10 80,20"></polygon>
                    <polygon points="90,50 80,50"></polygon>
                    <polygon points="80,80 70,70"></polygon>
                  </svg>
                </div>
              </div>
            </template>
            <template v-else-if="actionShow">
              <div class="actions">
                <a href="javascript:void(0)" class="action-item edit-rate" @click="$emit('edit', rate)">
                  <img loading="lazy" :src="'/website/assets/img/icons/edit.png'" alt="edit" />
                </a>
                <a href="javascript:void(0)" class="action-item delete-rate" @click="$emit('delete', rate)">
                  <img loading="lazy" :src="'/website/assets/img/icons/delete.png'" alt="delete" />
                </a>
            </div>
            </template>
            <template v-else-if="placeShow">
              <!-- Mini Place Image & Title for non-user view -->
              <div class="item_image_mini">
                <div class="image">
                  <NuxtLink :to="placeLink">
                    <img
                      loading="lazy"
                      :src="rating.place.main_image || '/website/assets/img/default-featured-image.jpeg'"
                      :alt="`تقييم ${rating.place.name || '-'}`"
                    />
                  </NuxtLink>
                </div>
                <div class="title">
                  <h3>
                    <NuxtLink :to="placeLink">{{ rating.place.name || '-' }}</NuxtLink>
                  </h3>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>


      <!-- Dropdown Menu (for userShow view only) -->
      <template v-if="userShow">
        <div class="card_filter_menu">
          <a
            href="javascript:void(0)"
            class="section_subtitle dropdown-toggle"
            
            id="dropdownMenuButton"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <svg
              id="more_linear"
              data-name="more/linear"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <g id="vuesax_linear_more" data-name="vuesax/linear/more">
                <g id="more">
                  <path
                    id="Vector"
                    d="M2,0A2,2,0,1,0,4,2,2.006,2.006,0,0,0,2,0Z"
                    transform="translate(3 10)"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    id="Vector-2"
                    data-name="Vector"
                    d="M2,0A2,2,0,1,0,4,2,2.006,2.006,0,0,0,2,0Z"
                    transform="translate(17 10)"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    id="Vector-3"
                    data-name="Vector"
                    d="M2,0A2,2,0,1,0,4,2,2.006,2.006,0,0,0,2,0Z"
                    transform="translate(10 10)"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    id="Vector-4"
                    data-name="Vector"
                    d="M0,0H24V24H0Z"
                    fill="none"
                    opacity="0"
                  />
                </g>
              </g>
            </svg>
          </a>
          <ul class="dropdown-menu dropdown-menu-start" aria-labelledby="dropdownMenuButton">
            <li>
              <a href="javascript:void(0)" class="share-rate dropdown-item" @click.prevent="handleReport">
                <svg xmlns="http://www.w3.org/2000/svg" width="18.032" height="18.032" viewBox="0 0 18.032 18.032">
                  <path id="flag-alt" d="M15.3,5.635l2.432-3.228A1.5,1.5,0,0,0,16.527,0H3.005A3.008,3.008,0,0,0,0,3.005V17.281a.751.751,0,1,0,1.5,0V11.27H16.528a1.5,1.5,0,0,0,1.2-2.407L15.3,5.635ZM1.5,9.767V3.005a1.5,1.5,0,0,1,1.5-1.5H16.528l-2.772,3.68a.752.752,0,0,0,0,.9l2.772,3.68H1.5Z" fill="#777e90"/>
                </svg>

                ابلاغ عن تقييم
              </a>
            </li>
            <li>
              <a href="javascript:void(0)" class="share-rate dropdown-item" @click="copyUrl">
                <svg id="link-2_linear" data-name="link-2/linear" xmlns="http://www.w3.org/2000/svg" width="18.032" height="18.032" viewBox="0 0 24 24">
                  <g id="vuesax_linear_link-2" data-name="vuesax/linear/link-2">
                    <g id="link-2">
                      <path id="Vector" d="M9.814,0a5.749,5.749,0,1,1-8.13,0" transform="translate(3.246 10.94)" fill="none" stroke="#777e90" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                      <path id="Vector-2" data-name="Vector" d="M1.755,10.249a6,6,0,1,1,8.49,0" transform="translate(8.835 3.161)" fill="none" stroke="#777e90" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                      <path id="Vector-3" data-name="Vector" d="M0,0H24V24H0Z" transform="translate(24 24) rotate(180)" fill="none" opacity="0"/>
                    </g>
                  </g>
                </svg>

                مشاركة التقييم
              </a>
            </li>
          </ul>
          <span v-if="copyMessage" id="copy-message" class="copy-message">
            {{ copyMessage }}
          </span>
        </div>
      </template>
    </div>
  </div>

</template>
<style>

.animating {
  animation: pop 0.5s ease-in-out;
}
@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

</style>